import type { Bounds, LatLng } from "./types";

const EARTH_RADIUS_METERS = 6371008.8;

export interface GridGeometryCell {
  id: string;
  polygon: LatLng[];
  centroid: LatLng;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function toRadians(value: number): number {
  return (value * Math.PI) / 180;
}

export function normalizePolygon(points: LatLng[]): LatLng[] {
  if (points.length === 0) {
    return points;
  }

  const normalized = points.map((point) => ({
    lat: Number(point.lat),
    lng: Number(point.lng),
  }));
  const first = normalized[0];
  const last = normalized[normalized.length - 1];

  if (first.lat !== last.lat || first.lng !== last.lng) {
    normalized.push({ ...first });
  }

  return isCounterClockwise(normalized) ? normalized : normalized.reverse();
}

export function isCounterClockwise(points: LatLng[]): boolean {
  return signedArea(points) > 0;
}

export function signedArea(points: LatLng[]): number {
  if (points.length < 3) {
    return 0;
  }

  let area = 0;

  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    area += current.lng * next.lat - next.lng * current.lat;
  }

  return area / 2;
}

export function rectanglePolygon(bounds: Bounds): LatLng[] {
  return normalizePolygon([
    { lat: bounds.south, lng: bounds.west },
    { lat: bounds.south, lng: bounds.east },
    { lat: bounds.north, lng: bounds.east },
    { lat: bounds.north, lng: bounds.west },
  ]);
}

export function boundsFromPolygon(polygon: LatLng[]): Bounds {
  const latitudes = polygon.map((point) => point.lat);
  const longitudes = polygon.map((point) => point.lng);

  return {
    north: Math.max(...latitudes),
    south: Math.min(...latitudes),
    east: Math.max(...longitudes),
    west: Math.min(...longitudes),
  };
}

export function centroidFromPolygon(polygon: LatLng[]): LatLng {
  const uniquePoints = polygon.slice(0, -1);
  const lat =
    uniquePoints.reduce((total, point) => total + point.lat, 0) /
    uniquePoints.length;
  const lng =
    uniquePoints.reduce((total, point) => total + point.lng, 0) /
    uniquePoints.length;

  return { lat, lng };
}

export function createGridCells(
  bounds: Bounds,
  rows: number,
  cols: number,
): GridGeometryCell[] {
  const cells: GridGeometryCell[] = [];
  const latStep = (bounds.north - bounds.south) / rows;
  const lngStep = (bounds.east - bounds.west) / cols;

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const cellBounds: Bounds = {
        north: bounds.north - latStep * row,
        south: bounds.north - latStep * (row + 1),
        west: bounds.west + lngStep * col,
        east: bounds.west + lngStep * (col + 1),
      };
      const polygon = rectanglePolygon(cellBounds);

      cells.push({
        id: `r${row + 1}c${col + 1}`,
        polygon,
        centroid: centroidFromPolygon(polygon),
      });
    }
  }

  return cells;
}

export function getCircleBounds(center: LatLng, radiusMeters: number): Bounds {
  const latDelta = (radiusMeters / EARTH_RADIUS_METERS) * (180 / Math.PI);
  const lngDelta =
    latDelta / Math.max(Math.cos(toRadians(center.lat)), 0.01);

  return {
    north: center.lat + latDelta,
    south: center.lat - latDelta,
    east: center.lng + lngDelta,
    west: center.lng - lngDelta,
  };
}

export function circleAreaSquareMeters(radiusMeters: number): number {
  return Math.PI * radiusMeters * radiusMeters;
}

export function polygonAreaSquareMeters(polygon: LatLng[]): number {
  if (polygon.length < 4) {
    return 0;
  }

  const points = polygon.slice(0, -1);
  const averageLat =
    points.reduce((total, point) => total + point.lat, 0) / points.length;
  const metersPerDegreeLat = (Math.PI * EARTH_RADIUS_METERS) / 180;
  const metersPerDegreeLng =
    ((Math.PI * EARTH_RADIUS_METERS) / 180) * Math.cos(toRadians(averageLat));
  let area = 0;

  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    const currentX = current.lng * metersPerDegreeLng;
    const currentY = current.lat * metersPerDegreeLat;
    const nextX = next.lng * metersPerDegreeLng;
    const nextY = next.lat * metersPerDegreeLat;
    area += currentX * nextY - nextX * currentY;
  }

  return Math.abs(area / 2);
}
